import {useRef, useState, useEffect} from "react";

/**
 * Edit an address using Google Places autocompletion.
 * We want to ensure all future addresses are captured via Google to reduce the chance of bad data.
 * Some fields are editable in case Google data is wrong or incomplete.
 */
export function EditAddressForm({uuid, targetUUID, addressType}) {
    const autoCompleteRef = useRef()
    const inputRef = useRef()

    const [firstLine, setFirstLine] = useState('')
    const [secondLine, setSecondLine] = useState('')
    const [thirdLine, setThirdLine] = useState('')
    const [city, setCity] = useState('')
    const [province, setProvince] = useState('')
    const [postalCode, setPostalCode] = useState('')
    const [country, setCountry] = useState('')
    const [countryCode, setCountryCode] = useState('')
    const [latitude, setLatitude] = useState('')
    const [longitude, setLongitude] = useState('')
    const [googlePlacesId, setGooglePlacesId] = useState('')

    const options = {
        componentRestrictions: {country: 'za'},
        fields: ["address_components", "geometry", "place_id"],
        types: ["address"]
    }

    useEffect(() => {
        autoCompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current,
            options
        )
        autoCompleteRef.current.addListener("place_changed", async function () {
            const place = await autoCompleteRef.current.getPlace()
            const structuredAddress = getStructuredAddress(place)

            setFirstLine(structuredAddress.firstLine)
            setSecondLine(structuredAddress.secondLine)
            setThirdLine(structuredAddress.thirdLine)
            setPostalCode(structuredAddress.postalCode)
            setCity(structuredAddress.city)
            setProvince(structuredAddress.province)
            setCountry(structuredAddress.country)
            setCountryCode(structuredAddress.countryCode)
            setLatitude(structuredAddress.latitude)
            setLongitude(structuredAddress.longitude)
            setGooglePlacesId(place.place_id)
        })
    }, [])

    const getStructuredAddress = (place) => {
        return {
            firstLine: getFirstLine(place),
            secondLine: getAddressComponent(place, "sublocality"),
            thirdLine: getAddressComponent(place, "administrative_area_level_3"),
            postalCode: getAddressComponent(place, "postal_code"),
            city: getAddressComponent(place, "locality"),
            province: getAddressComponent(place, "administrative_area_level_1"),
            country: getAddressComponent(place, "country"),
            countryCode: getAddressComponent(place, "country", false),
            latitude: place.geometry?.location?.lat() || null,
            longitude: place.geometry?.location?.lng() || null
        }
    }

    const getAddressComponent = (place, name, longName = true) => {
        if (place.address_components) {
            const match = place.address_components.find(component => {
                return component.types.includes(name)
            })
            if (match) {
                return longName ? match.long_name : match.short_name
            }
        }
        return ''
    }

    const getFirstLine = (place) => {
        const streetNumber = getAddressComponent(place, "street_number")
        const streetName = getAddressComponent(place, "route")
        if (streetNumber && streetName) {
            return `${streetNumber} ${streetName}`
        }
        if (streetName) {
            return streetName
        }
        return ''
    }

    const onSave = () => {
        const payload = {
            firstLine,
            secondLine,
            thirdLine,
            city,
            province,
            postalCode,
            countryCode,
            latitude,
            longitude,
            uuid,
            addressType,
            googlePlacesId,
            targetUUID
        }

        $.ajax({
            url: `/api/addresses/${uuid}/`,
            method: 'POST',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            data: JSON.stringify(payload)
        }).done((response) => {
            radio('hideLoader').broadcast()
            if (response.status === 200) {
                radio('showAlert').broadcast('success', 'Address saved')
            } else {
                radio('showAlert').broadcast('warning', `Action failed: ${response.message} (${response.status})`)
            }
        }).fail((response, textStatus) => {
            radio('showAlert').broadcast('danger', textStatus)
        })
    }

    return (
        <div>
            <h1>Edit Address</h1>
            <div className="form-horizontal">
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">Search</label>
                    <div className="col-lg-5">
                        <input className="form-control" ref={inputRef}/>
                    </div>
                </div>
                <br/>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">Address Type</label>
                    <div className="col-lg-5">
                        <input className="form-control" value={addressType.titleize()} disabled/>
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">Address</label>
                    <div className="col-lg-5">
                        <input className="form-control" value={firstLine} disabled/>
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label"></label>
                    <div className="col-lg-5">
                        <input
                            className="form-control"
                            value={secondLine}
                            onChange={e => setSecondLine(e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label"></label>
                    <div className="col-lg-5">
                        <input
                            className="form-control"
                            value={thirdLine}
                            onChange={e => setThirdLine(e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">Postal Code</label>
                    <div className="col-lg-5">
                        <input
                            className="form-control"
                            value={postalCode}
                            onChange={e => setPostalCode(e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">City</label>
                    <div className="col-lg-5">
                        <input
                            className="form-control"
                            value={city}
                            onChange={e => setCity(e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">Province</label>
                    <div className="col-lg-5">
                        <input className="form-control" value={province} disabled/>
                    </div>
                </div>
                <div className="form-field form-group">
                    <label className="col-lg-3 control-label">Country</label>
                    <div className="col-lg-5">
                        <input className="form-control" value={country} disabled/>
                    </div>
                </div>
                <div className="form-actions">
                    <div className="row">
                        <div className="col-lg-9 col-lg-offset-3 edit-actions">
                            <button
                                data-style="zoom-out"
                                data-spinner-size="32"
                                onClick={onSave}
                                className="btn ladda-button btn-primary">
                                <span className="ladda-label">Save</span>
                                <span className="ladda-spinner"></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
